import {
  API_URL,
  v,
  put,
  debug,
  request,
  takeEvery,
  createDoRequestAction,
  assertTotalSchema,
  SagaIterator,
  call,
} from "../../lib";
import { v4 } from "uuid";
import { baseRequest, BaseRequestResult } from "../../lib/request";
import { SessionScope, isSessionScopeCurrent } from "../../lib/auth/session_scope";
import { SavedQuestionnaireTasksOutcome } from "../../lib/questionnaire_draft/coordinator";
import {
  doREQUEST_ERROR,
  doREQUEST_COMPLETE,
  doMETHOD_SET,
  doPENDING_REQUEST_DELETE,
  doREQUEST_SKIP,
} from "../state";
import { checkRequestPending } from "./check_pending_request";
import { getToken } from "./token_fetcher";
import { doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT } from "./identity_refresh_token_submit";

export const REQUEST_METHOD_FETCH = "REQUEST_METHOD_FETCH";
export const doREQUEST_METHOD_FETCH = createDoRequestAction(REQUEST_METHOD_FETCH);

export const requestMethodFetchActionCreators = {
  doREQUEST_METHOD_FETCH,
};

export function* requestMethodFetch({ payload, setRequestResult }: any): SagaIterator {
  debug("saga*requestMethodFetch");

  const sameRequestPending = yield call(checkRequestPending, REQUEST_METHOD_FETCH, payload);
  if (sameRequestPending === true) {
    return yield put(
      doREQUEST_SKIP({
        fromType: REQUEST_METHOD_FETCH,
        requestId: payload.requestId,
      }),
    );
  }

  try {
    assertTotalSchema({
      payload,
      schema: v.object({
        requestId: v.string().uuid().exist(),
        methodId: v.string().uuid().exist(),
      }),
    });
  } catch (error) {
    yield put(
      doPENDING_REQUEST_DELETE({ requestType: REQUEST_METHOD_FETCH, requestId: payload.requestId }),
    );
    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_METHOD_FETCH,
          requestId: payload.requestId,
          error,
        },
        setRequestResult,
      ),
    );
  }

  const { methodId } = payload;

  const accessToken = yield call(getToken);

  const result = yield request({
    method: "post",
    url: `${API_URL}/v1/web/method-fetch`,
    accessToken,
    data: {
      methodId,
    },
  });

  if (!result.success || result.error) {
    const error = result.error || new Error("request did not succeed");
    debug(error);
    yield put(
      doPENDING_REQUEST_DELETE({ requestType: REQUEST_METHOD_FETCH, requestId: payload.requestId }),
    );

    return yield put(
      doREQUEST_ERROR(
        {
          fromType: REQUEST_METHOD_FETCH,
          fromPayload: payload,
          requestId: payload.requestId,
          url: "/v1/web/method-fetch",
          ...result,
          error,
        },
        setRequestResult,
      ),
    );
  }

  yield put(doMETHOD_SET(result.payload));

  yield put(
    doPENDING_REQUEST_DELETE({ requestType: REQUEST_METHOD_FETCH, requestId: payload.requestId }),
  );

  yield put(
    doREQUEST_COMPLETE(
      {
        fromType: REQUEST_METHOD_FETCH,
        requestId: payload.requestId,
        ...result,
      },
      setRequestResult,
    ),
  );
}

export function* methodFetchSaga() {
  yield takeEvery(REQUEST_METHOD_FETCH, requestMethodFetch);
}

// The method fetched again before a recovery copy is offered, so the copy is judged against the saved
// tasks as they are now. Unlike REQUEST_METHOD_FETCH it belongs to the session that asked: the scope is
// checked before the token wait, after it and before the method is published, so a read made before a
// sign-out or a sign-in as someone else neither goes out with the next session's token nor lands in
// its state. Its answer goes straight back to the caller, never into request state.
export function* fetchMethodForDraftRestore({
  scope,
  methodId,
}: {
  scope: SessionScope;
  methodId: string;
}): Generator<any, SavedQuestionnaireTasksOutcome, any> {
  if (!isSessionScopeCurrent(scope)) return { kind: "stale" };
  const accessToken: string | null = yield call(getToken);
  if (!accessToken || !isSessionScopeCurrent(scope)) return { kind: "stale" };

  const result: BaseRequestResult = yield call(() =>
    baseRequest({
      method: "post",
      url: `${API_URL}/v1/web/method-fetch`,
      accessToken,
      data: { methodId },
    }),
  );
  if (!isSessionScopeCurrent(scope)) return { kind: "stale" };

  if (!result.success) {
    debug("saga*fetchMethodForDraftRestore failed", { statusCode: result.statusCode });
    // the refresh decides whether the session is over; the researcher can ask again either way
    if (result.statusCode === 401) {
      yield put(doREQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT({ requestId: v4() }));
      return { kind: "retry" };
    }
    return result.statusCode === null || result.statusCode >= 500
      ? { kind: "retry" }
      : { kind: "refused" };
  }

  yield put(doMETHOD_SET(result.payload));
  return { kind: "ok", taskList: result.payload?.taskList ?? [] };
}
